/**
 * Tree (WAI-ARIA tree pattern) with lazy children, and CabinetTree on top of it.
 *
 * Keyboard: Up/Down move, Right opens (or goes to the first child), Left closes (or goes
 * to the parent), Home/End jump, Enter/Space selects, typing a letter jumps to the next
 * visible item starting with it. One tab stop (roving tabindex): the selected item, else
 * the first.
 *
 * Children load only when a node is opened: pass `useChildren(node, enabled)` — a hook
 * (e.g. a React Query wrapper) that returns { data, isLoading, isError }.
 */
import { ChevronRight, Eye, FolderClosed, FolderOpen, Library, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useFolders } from '../../lib/queries';
import type { Cabinet, Folder, Grant } from '../../lib/types';
import { cx } from './core';

export interface TreeNode {
  id: string;
  label: string;
  hasChildren: boolean;
  icon?: (open: boolean) => ReactNode;
  /** Trailing text (count, "view only"). */
  meta?: ReactNode;
  /** Free data for useChildren / onSelect. */
  data?: unknown;
}

export interface ChildrenResult {
  data?: TreeNode[];
  isLoading: boolean;
  isError: boolean;
}

interface TreeProps {
  label: string;
  roots: TreeNode[];
  useChildren: (node: TreeNode, enabled: boolean) => ChildrenResult;
  selectedId?: string | null;
  onSelect: (node: TreeNode) => void;
  /** Ids open on first render (e.g. the path to the selected folder). */
  defaultOpen?: string[];
  dense?: boolean;
  className?: string;
}

export function Tree({ label, roots, useChildren, selectedId, onSelect, defaultOpen = [], dense, className }: TreeProps) {
  const [open, setOpen] = useState<Set<string>>(() => new Set(defaultOpen));
  const ref = useRef<HTMLUListElement>(null);
  const defaultKey = defaultOpen.join('|');
  useEffect(() => {
    if (defaultOpen.length) setOpen((o) => new Set([...o, ...defaultOpen]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultKey]);

  const toggle = useCallback((id: string, to?: boolean) => {
    setOpen((o) => {
      const n = new Set(o);
      const want = to ?? !n.has(id);
      if (want) n.add(id);
      else n.delete(id);
      return n;
    });
  }, []);

  const items = () => Array.from(ref.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') ?? []);
  const focusItem = (el: HTMLElement | undefined) => {
    if (!el) return;
    items().forEach((i) => (i.tabIndex = -1));
    el.tabIndex = 0;
    el.focus();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>('[role="treeitem"]');
    if (!el) return;
    const list = items();
    const i = list.indexOf(el);
    const id = el.dataset.id!;
    const expandable = el.getAttribute('aria-expanded');
    switch (e.key) {
      case 'ArrowDown':
        focusItem(list[i + 1]);
        break;
      case 'ArrowUp':
        focusItem(list[i - 1]);
        break;
      case 'Home':
        focusItem(list[0]);
        break;
      case 'End':
        focusItem(list[list.length - 1]);
        break;
      case 'ArrowRight':
        if (expandable === 'false') toggle(id, true);
        else if (expandable === 'true') focusItem(list[i + 1]?.dataset.parent === id ? list[i + 1] : undefined);
        break;
      case 'ArrowLeft':
        if (expandable === 'true') toggle(id, false);
        else focusItem(list.find((x) => x.dataset.id === el.dataset.parent));
        break;
      case 'Enter':
      case ' ':
        el.click();
        break;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          const k = e.key.toLowerCase();
          const after = [...list.slice(i + 1), ...list.slice(0, i)];
          focusItem(after.find((x) => (x.dataset.label ?? '').toLowerCase().startsWith(k)));
          break;
        }
        return;
    }
    e.preventDefault();
  };

  const tabStop = selectedId ?? roots[0]?.id;
  return (
    <ul ref={ref} role="tree" aria-label={label} onKeyDown={onKeyDown} className={cx('text-sm', className)}>
      {roots.map((n, i) => (
        <Branch key={n.id} node={n} level={1} pos={i + 1} size={roots.length} parent={null} open={open} toggle={toggle} useChildren={useChildren} selectedId={selectedId} tabStop={tabStop} onSelect={onSelect} dense={dense} />
      ))}
    </ul>
  );
}

interface BranchProps {
  node: TreeNode;
  level: number;
  pos: number;
  size: number;
  parent: string | null;
  open: Set<string>;
  toggle: (id: string, to?: boolean) => void;
  useChildren: TreeProps['useChildren'];
  selectedId?: string | null;
  tabStop?: string;
  onSelect: (n: TreeNode) => void;
  dense?: boolean;
}

function Branch({ node, level, pos, size, parent, open, toggle, useChildren, selectedId, tabStop, onSelect, dense }: BranchProps) {
  const isOpen = node.hasChildren && open.has(node.id);
  const kids = useChildren(node, isOpen);
  const selected = selectedId === node.id;
  return (
    <li role="none">
      <div
        role="treeitem"
        data-id={node.id}
        data-parent={parent ?? undefined}
        data-label={node.label}
        aria-level={level}
        aria-posinset={pos}
        aria-setsize={size}
        aria-expanded={node.hasChildren ? isOpen : undefined}
        aria-selected={selected}
        tabIndex={tabStop === node.id ? 0 : -1}
        onClick={() => onSelect(node)}
        className={cx(
          'group relative flex cursor-pointer select-none items-center gap-1.5 rounded-tag pr-2 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-brand',
          dense ? 'min-h-9' : 'min-h-touch',
          selected ? 'bg-brand-soft font-semibold text-brand' : 'text-ink hover:bg-sunken',
        )}
        style={{ paddingLeft: `${(level - 1) * 16 + 4}px` }}
      >
        {node.hasChildren ? (
          <span
            aria-hidden
            onClick={(e) => {
              e.stopPropagation();
              toggle(node.id);
            }}
            className="grid h-7 w-7 shrink-0 place-items-center rounded text-muted hover:bg-line/60 hover:text-ink"
          >
            {kids.isLoading && isOpen ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className={cx('h-4 w-4 transition-transform', isOpen && 'rotate-90')} />}
          </span>
        ) : (
          <span aria-hidden className="w-7 shrink-0" />
        )}
        {node.icon?.(isOpen)}
        <span className="min-w-0 flex-1 truncate">{node.label}</span>
        {node.meta && <span className="shrink-0 text-xs font-normal text-muted">{node.meta}</span>}
      </div>
      {isOpen && (
        <ul role="group">
          {kids.isError && (
            <li role="none" className="py-1 text-xs text-danger" style={{ paddingLeft: `${level * 16 + 36}px` }}>
              Could not load the folders.
            </li>
          )}
          {kids.data?.length === 0 && (
            <li role="none" className="py-1 text-xs text-muted" style={{ paddingLeft: `${level * 16 + 36}px` }}>
              No folders
            </li>
          )}
          {kids.data?.map((k, i) => (
            <Branch key={k.id} node={k} level={level + 1} pos={i + 1} size={kids.data!.length} parent={node.id} open={open} toggle={toggle} useChildren={useChildren} selectedId={selectedId} tabStop={tabStop} onSelect={onSelect} dense={dense} />
          ))}
        </ul>
      )}
    </li>
  );
}

/* ---------------------------------------------------------------- cabinets */

/** Node ids: "c:<cabinetId>" and "f:<cabinetId>:<folderId>". */
export const cabinetNodeId = (cabinetId: string) => `c:${cabinetId}`;
export const folderNodeId = (cabinetId: string, folderId: string) => `f:${cabinetId}:${folderId}`;
export function parseNodeId(id: string): { cabinetId: string; folderId: string | null } {
  const [kind, cabinetId, folderId] = id.split(':');
  return { cabinetId, folderId: kind === 'f' ? folderId : null };
}

const grantMeta = (grant: Grant, n?: number): ReactNode =>
  grant === 'view' ? (
    <span className="inline-flex items-center gap-1" title="You can view, not file, here">
      <Eye aria-hidden className="h-3.5 w-3.5" />
      <span className="sr-only">view only</span>
    </span>
  ) : n !== undefined ? (
    <span className="tabular">{n}</span>
  ) : null;

const folderIcon = (open: boolean) => (open ? <FolderOpen aria-hidden className="h-4 w-4 shrink-0 text-manila" /> : <FolderClosed aria-hidden className="h-4 w-4 shrink-0 text-manila" />);

function cabinetToNode(c: Cabinet): TreeNode {
  return { id: cabinetNodeId(c.id), label: c.name, hasChildren: (c.folderCount ?? 1) > 0, icon: () => <Library aria-hidden className="h-4 w-4 shrink-0 text-muted" />, meta: grantMeta(c.grant, c.documentCount), data: { cabinetId: c.id, folderId: null } };
}

function folderToNode(f: Folder): TreeNode {
  return { id: folderNodeId(f.cabinetId, f.id), label: f.name, hasChildren: f.hasChildren, icon: folderIcon, meta: grantMeta(f.grant, f.documentCount), data: { cabinetId: f.cabinetId, folderId: f.id } };
}

function useFolderChildren(node: TreeNode, enabled: boolean): ChildrenResult {
  const { cabinetId, folderId } = node.data as { cabinetId: string; folderId: string | null };
  const q = useFolders(cabinetId, folderId, enabled);
  return { data: q.data?.map(folderToNode), isLoading: q.isLoading && enabled, isError: q.isError };
}

/**
 * Cabinets and folders the user may see (the API returns only granted ones). Folders
 * load when opened. View-only places show an eye, others the document count.
 */
export function CabinetTree({ cabinets, selected, onSelect, defaultOpen, dense, label = 'Cabinets', className }: { cabinets: Cabinet[]; selected?: string | null; onSelect: (p: { cabinetId: string; folderId: string | null; label: string }) => void; defaultOpen?: string[]; dense?: boolean; label?: string; className?: string }) {
  return (
    <Tree
      label={label}
      roots={cabinets.map(cabinetToNode)}
      useChildren={useFolderChildren}
      selectedId={selected}
      defaultOpen={defaultOpen}
      dense={dense}
      className={className}
      onSelect={(n) => onSelect({ ...(n.data as { cabinetId: string; folderId: string | null }), label: n.label })}
    />
  );
}
